import { cn } from '@/lib/utils';
import { AlertCircle } from 'lucide-react';
import { useContext, useMemo } from 'react';

import {
  ChainlitContext,
  useChatSession,
  useConfig
} from '@chainlit/react-client';

import { BrandLogo } from '@/components/BrandLogo';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';

interface Props {
  author?: string;
  className?: string;
  hide?: boolean;
  isError?: boolean;
}

const MessageAvatar = ({ author, className, hide, isError }: Props) => {
  const apiClient = useContext(ChainlitContext);
  const { chatProfile } = useChatSession();
  const { config } = useConfig();

  const selectedChatProfile = useMemo(() => {
    return config?.chatProfiles.find((profile) => profile.name === chatProfile);
  }, [config, chatProfile]);

  const normalizedAuthor = author?.toLocaleLowerCase();
  const configuredAssistantName = config?.ui.name?.toLocaleLowerCase();
  const isAssistant =
    !normalizedAuthor ||
    normalizedAuthor === configuredAssistantName ||
    normalizedAuthor === 'assistant' ||
    normalizedAuthor === 'ipma copilot';
  const useBrandLogo = isAssistant && !selectedChatProfile?.icon;

  const avatarUrl = useMemo(() => {
    if (config?.ui?.default_avatar_file_url)
      return config?.ui?.default_avatar_file_url;
    if (isAssistant && selectedChatProfile?.icon) {
      return selectedChatProfile.icon;
    }
    return apiClient?.buildEndpoint(`/avatars/${author || 'default'}`);
  }, [apiClient, selectedChatProfile, config, author]);

  if (isError) {
    return (
      <span
        className={cn(
          'message-avatar inline-block',
          className,
          hide && 'invisible'
        )}
      >
        <AlertCircle className="mt-1 size-8 rounded-full bg-destructive/10 p-1.5 text-destructive" />
      </span>
    );
  }

  if (useBrandLogo) {
    return (
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className={cn(
                'message-avatar assistant-brand-avatar inline-flex',
                className,
                hide && 'invisible'
              )}
            >
              <BrandLogo size="avatar" variant="mark" />
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <p>{author || config?.ui.name || 'IPMA Copilot'}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <span
      className={cn(
        'message-avatar inline-block',
        className,
        hide && 'invisible'
      )}
    >
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Avatar className="size-8 shrink-0 border border-border/80 bg-card shadow-sm">
              <AvatarImage
                src={avatarUrl}
                alt={`Avatar for ${author || 'default'}`}
                className="bg-transparent"
              />
              <AvatarFallback className="bg-primary/10 text-primary">
                <Skeleton className="h-full w-full rounded-full" />
              </AvatarFallback>
            </Avatar>
          </TooltipTrigger>
          <TooltipContent>
            <p>{author}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </span>
  );
};

export { MessageAvatar };
